/// <amd-module name="Environments/Pipelines/Run/EnvironmentsRunTabContent" />
import React = require("react");

import { IEnvironmentRunObject, IEnvironmentRunJobRecord } from "Environments/Client/EnvironmentContracts";
import { getEnvironmentPageUrl, getResourcePageUrl, getResourceRoute } from "Environments/EnvironmentsCommon/Link";
import { getJobStatusIndicatorData } from "Environments/RunsCommon/Indicator";
import { NavConstants, getBuildUrl, RouteParameters } from "Environments/RunsCommon/Linking";
import { Area, FeatureNames, Action } from "Environments/RunsCommon/Utils";
import * as Resources from "Environments/Pipelines/Run/Resources";

import { ObservableArray } from "VSS/Core/Observable";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { VssComponent, IVssComponentProperties, IVssComponentContext } from "VSS/Platform/Layout";

import { Card } from "VSSUI/Card";
import { Duration } from "VSSUI/Duration";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Icon } from "VSSUI/Icon";
import { Status, StatusSize } from "VSSUI/Status";
import { Table, ITableColumn, TableColumnStyle, SimpleTableCell, ITableRow } from "VSSUI/Table";
import { Tooltip } from "VSSUI/TooltipEx";
import { publishTelemetry } from "Environments/EnvironmentsCommon/Telemetry";

interface IEnvironmentsTabProps extends IVssComponentProperties {
    environmentsRuns: IEnvironmentRunObject[];
}

export class EnvironmentsTabComponent extends VssComponent<IEnvironmentsTabProps> {

    constructor(props: IEnvironmentsTabProps, context: IVssComponentContext) {
        super(props, context);

        const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
        const buildId = historyService.getState()[RouteParameters.BuildId];
        publishTelemetry(context.pageContext, Area, FeatureNames.PipelineRunsView, { "Action": Action.EnvironmentTabNavigation, "BuildId": buildId});
    }

    public render(): JSX.Element {
        const content: JSX.Element[] = (
            this.props.environmentsRuns.map((environment: IEnvironmentRunObject, index: number) => {
                return <EnvironmentCard key={"env-pipeline-card-"+index} environmentRun={environment} />;
            })
        );
        return <>{...content}</>;
    }
}

interface IEnvironmentCardProps extends IVssComponentProperties {
    environmentRun: IEnvironmentRunObject;
}

class EnvironmentCard extends VssComponent<IEnvironmentCardProps> {

    public render(): JSX.Element {
        return (
            <Card
                className="bolt-table-card card-spacer"
                titleProps={{ text: this.props.environmentRun.environmentReference.name }}
                headerCommandBarItems={this._commandBarItems}
                key={"pipeline-environment-"+this.props.environmentRun.environmentReference.id}
                contentProps={{ contentPadding: false }}
            >
                {this._jobsTable()}
            </Card>
        )
    }

    private _jobsTable(): JSX.Element {
        const itemProvider = new ObservableArray(this.props.environmentRun.jobs);

        return <Table
            itemProvider={itemProvider}
            columns={this._jobsTableColumns}
            onActivate={this._onJobClick}
            ariaLabel={this.props.environmentRun.environmentReference.name}
        />
    }

    private _renderNameCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IEnvironmentRunJobRecord>, tableItem: IEnvironmentRunJobRecord): JSX.Element => {
        const status = getJobStatusIndicatorData(tableItem.result);

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontWeightSemiBold fontSizeM scroll-hidden">
                <Tooltip text={status.label}>
                    <span className="flex-row">
                        <Status {...status.statusProps} className="envs-icon-margin" size={StatusSize.m} />
                    </span>
                </Tooltip>
                <div className="flex-row scroll-hidden">
                    <Tooltip overflowOnly={true} text={tableItem.jobName}>
                        <span className="text-ellipsis">{tableItem.jobName}</span>
                    </Tooltip>
                </div>
            </SimpleTableCell>
        );
    }

    private _renderResourceCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IEnvironmentRunJobRecord>, tableItem: IEnvironmentRunJobRecord): JSX.Element => {
        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="bolt-table-cell-content-with-inline-link no-v-padding fontSizeM">
                {this._getResourceCellContent(tableItem)}
            </SimpleTableCell>
        );
    }

    private _renderDurationCell = (rowIndex: number, columnIndex: number, tableColumn: ITableColumn<IEnvironmentRunJobRecord>, tableItem: IEnvironmentRunJobRecord): JSX.Element => {
        if (!tableItem.startTime) {
            return <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontSizeM scroll-hidden" />
        }

        return (
            <SimpleTableCell columnIndex={columnIndex} tableColumn={tableColumn} key={"col-" + columnIndex} contentClassName="fontSizeM scroll-hidden secondary-text">
                <Icon iconName="Clock" className="icon-margin" />
                <Duration startDate={tableItem.startTime} endDate={tableItem.finishTime} />
            </SimpleTableCell>
        );
    }

    private _getResourceCellContent(deploymentRecord: IEnvironmentRunJobRecord): React.ReactNode {
        if (deploymentRecord.resourceReference) {
            const resourceName = deploymentRecord.resourceReference.name;
            const resourceRoute = getResourceRoute(deploymentRecord.resourceReference.type);
            const resourceUrl = getResourcePageUrl(this.context.pageContext, this.props.environmentRun.environmentReference.id, deploymentRecord.resourceReference.id, resourceRoute);

            return (
                <Tooltip overflowOnly={true} text={resourceName}>
                    <FPSLink className="text-ellipsis bolt-table-link bolt-table-inline-link" excludeTabStop href={resourceUrl}>
                        {resourceName}
                    </FPSLink>
                </Tooltip>
            )
        }
        else {
            return undefined;
        }
    }

    private _onJobClick = (event: React.SyntheticEvent<HTMLElement>, tableRow: ITableRow<IEnvironmentRunJobRecord>) => {
        if (tableRow.data) {
            const historyService = this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
            const buildId = historyService.getState()[RouteParameters.BuildId];

            const pageContext = this.context.pageContext;
            let routes: { [key: string]: string } = {};
            routes[NavConstants.job] = tableRow.data.jobId;
            const url = getBuildUrl(pageContext, Number(buildId), routes);
            event.preventDefault();
            onClickFPS(pageContext, url, true, undefined);
        }
    }

    private readonly _commandBarItems: IHeaderCommandBarItem[] = [
        {
            important: true,
            id: "viewEnvironment"+ this.props.environmentRun.environmentReference.id,
            text: Resources.EnvironmentDetailsSubheader,
            onActivate: () => {
                onClickFPS(this.context.pageContext, getEnvironmentPageUrl(this.context.pageContext, this.props.environmentRun.environmentReference.id), true);
            }
        }
    ];

    private readonly _jobsTableColumns: ITableColumn<IEnvironmentRunJobRecord>[] = [
        {
            id: "name",
            width: 264,
            name: Resources.JobsColmnName,
            renderCell: this._renderNameCell,
            columnStyle: TableColumnStyle.Primary
        },
        {
            id: "resource",
            width: 264,
            name: Resources.ResourceColumnName,
            renderCell: this._renderResourceCell
        },
        {
            id: "duration",
            width: -100,
            name: Resources.DurationColumnName,
            renderCell: this._renderDurationCell
        }
    ];
}
